<template>
  <ChatWindowWrapper></ChatWindowWrapper>
</template>

<script>
  import ChatWindowWrapper from './views/chatWindowWrapper'
  import StorageEvent from './services/storageEvent'
  import '@/services/broadcastChannel/'
  import { emitEventToParentWindow, addAlphaInHexColor, getThemeDefaultColor } from './services/utils'
  import { eventType, customFontFamily } from './services/constants'
  import { getBrowserFontSizeSetting } from './services/customFontSize'

  export default {
    components: { ChatWindowWrapper },

    async created() {
      StorageEvent.addStorageListener()
      emitEventToParentWindow(eventType.TRIGGER_PENDING_EVENTS_BEFORE_INIT)
      await this.$store.dispatch('init')
      this.handleFontFamilyAndSizeChange()
      this.handleThemeColorChange(this.themeColor)
      this.handleUserChatAndBotLinksColorChange(this.userChatAndBotLinksColor)
      emitEventToParentWindow(eventType.TRIGGER_PENDING_EVENTS)
    },
    mounted() {
      // Only update browser font size setting on tab visibility change
      this._browserFontSizeSettingVisibilityHandler = () => {
        if (document.visibilityState === 'visible') {
          getBrowserFontSizeSetting(this.$store)
        }
      }
      document.addEventListener('visibilitychange', this._browserFontSizeSettingVisibilityHandler)

      if(this.isDisneyDemoBot) {
        const documentStyle = document.documentElement.style
        documentStyle.setProperty('--disney-gradient-color', this.$store.state.widgetConfig?.brandGPT?.primaryColor)
      }
    },
    beforeUnmount() {
      document.removeEventListener('visibilitychange', this._browserFontSizeSettingVisibilityHandler)
    },

    computed: {
      isDisneyDemoBot() {
        return this.$store.state.widgetConfig?.brandGPT?.enable || false
      },
      themeColor() {
        return this.$store.state.widgetConfig?.theme?.color
      },
      userChatAndBotLinksColor() {
        return this.$store.state.widgetConfig?.botResponseBubbleColor
      },
      globalBorderRadius() {
        return this.$store.state.widgetConfig?.borderRadius || 'medium'
      }
    },

    watch: {
      themeColor(value) {
        this.handleThemeColorChange(value)
      },
      userChatAndBotLinksColor(value) {
        this.handleUserChatAndBotLinksColorChange(value)
      },
      globalBorderRadius(value) {
        const radius = { 'none': 0, 'small': 4, 'medium': 8, 'large': 16};

        if(!(value in radius)) return;

        const radiusBaseVariant = radius[value];
        const isLarge = value === 'large'

        const documentStyle = document.documentElement.style
        documentStyle.setProperty('--radius-chat-outer-curve', `${radiusBaseVariant}px`)
        documentStyle.setProperty('--radius-chat-inner-icon', `${radiusBaseVariant}px`)
        documentStyle.setProperty('--radius-chat-header-icon', `${radiusBaseVariant + (isLarge ? 8 : 0)}px`) // adding 8 to make it 24 in case of large border radius
        documentStyle.setProperty('--radius-chat-inner-component', `${radiusBaseVariant + (isLarge ? 4 : 0)}px`) // adding 4 to make it 20 in case of large border radius
      }
    },

    methods: {
      handleFontFamilyAndSizeChange() {
        let fontFamily = this.$store.getters.getCustomFontFamily
        if (fontFamily !== 'Custom') {
          fontFamily = customFontFamily[fontFamily]
        } else {
          fontFamily = this.$store.getters.getUserDefinedFontFamily
        }
        if (this.$store.getters.isFontFamilyWFContentOverrideEnabled) {
          // Set the CSS variable without !important
          document.documentElement.style.setProperty('--font-family-WF-response', fontFamily)
          // Inject or update a style tag to apply !important via JS
          const styleId = 'attachment-intent-response-font-family-style'
          let styleTag = document.getElementById(styleId)
          if (styleTag) {
            styleTag.parentNode.removeChild(styleTag)
          }
          styleTag = document.createElement('style')
          styleTag.id = styleId
          styleTag.innerHTML = `.attachment-intent-response { font-family: var(--font-family-WF-response) !important; } .attachment-intent-response * { font-family: inherit !important; }`
          document.head.appendChild(styleTag)
        }
        // Set the non-WF variable without !important
        document.documentElement.style.setProperty('--font-family-non-WF-response', fontFamily)
      },
      handleThemeColorChange(value) {
      document.documentElement.style.setProperty(
        '--widget-primary-color',
        value || getThemeDefaultColor()
      )
      document.documentElement.style.setProperty(
        '--widget-primary-background-color',
        addAlphaInHexColor(
          value || getThemeDefaultColor('--widget-primary-background-color')
        )
      )
      document.documentElement.style.setProperty('--widget-text-color', this.$store.getters.getColor)
      },
      handleUserChatAndBotLinksColorChange(value) {
        const userMessageColorVar = '--user-chat-color'
        const botLinksColorVar = '--bot-links-color'
        document.documentElement.style.setProperty(
          userMessageColorVar,
          value || getThemeDefaultColor(userMessageColorVar)
        )
        document.documentElement.style.setProperty(
          botLinksColorVar,
          value || getThemeDefaultColor(botLinksColorVar)
        )
        document.documentElement.style.setProperty(
          '--button-links-hover-background-color',
          addAlphaInHexColor(
            value || getThemeDefaultColor('--button-links-hover-background-color')
          )
        )
      }
    }
  }
</script>
